import axios from "axios";
import isError from "lodash/isError";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import QS from "qs";
import PATHS, { API_PATHS } from "./paths";
import { getConfig } from "utils/env";
import { defaultHttps, omitQueryStringKeys } from "utils/url";
import { captureException } from "./error";
import { authBearerToken } from "./auth";
import { devLogger } from "./debug";
import { X_API_KEY, X_REFERER, X_USER_AGENT } from "utils/constants/header";
import { isTestRoute } from "./route";
import { getSubdomainRoot, subdomainParamsFromReq } from "utils/subdomain";
import {
  CHECKOUT_CONTENT_ERROR_CONTENT,
  CHECKOUT_SESSION_ERROR_CONTENT,
  CHECKOUT_CONTENT_NO_DATA_CONTENT
} from "utils/constants/error";
import { PAGE_KEYS, preparePageResponse } from "utils/render";
import { STRIPE_SESSION_ID_QS_PARAM } from "./constants/stripe";

const QS_OMISSION_KEYS = ["celebrate"];

export const getCheckoutScaffold = () => ({
  payment_method_types: ["card"],
  allow_promotion_codes: false,
  discounts: [],
  customer: "",
  customer_email: "",
  tax_rates: [],
  shipping_worldwide: false
  // payment_method_types: ["card"],
  // dynamic_tax_rates: []
  // billing_address_collection,
  // shipping_address_collection,
  // submit_type
});

/**
 * Get metadata and client account details
 */
export const renderLinksRoute = async ({ apiKey, url, referer, userAgent }) => {
  try {
    const headers = {
      [X_API_KEY]: apiKey
    };
    // proxy.referer
    // "x-referer": "https://l.instagram.com",
    // proxy.userAgent
    // "x-user-agent": "WhatsApp",
    if (referer) {
      headers[X_REFERER] = referer;
    }
    if (userAgent) {
      headers[X_USER_AGENT] = userAgent;
    }
    const { data } = await axios({
      method: "get",
      url,
      headers
    });

    return data;
  } catch (error) {
    return error;
  }
};

export const fetchPageResponse = async (url) => {
  try {
    const { data } = await axios.get(url);

    return data;
  } catch (error) {
    return error;
  }
};

export const renderStripeCheckoutError = async (url) => {
  try {
    const { data } = await axios.get(url);

    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Build the sesssion checkout params - ensure client side UI side effect params like celebrate are omitted
 * @param {Object} params
 * @param {Object} params.subdomain - app subdomain
 * @param {Object} params.price - price id
 * @param {Object} params.cancelUrl - optional cancel url, defaults to current location
 * @param {Object} params.checkout - checkout specific params like encrypted session
 * @param {Object} params.tracking - object of tracking params
 */
export const getSessionParams = ({
  subdomain,
  price,
  cancelUrl,
  checkout,
  tracking
}) => {
  return {
    subdomain,
    price,
    cancelUrl: omitQueryStringKeys(
      cancelUrl || window.location.href,
      QS_OMISSION_KEYS
    ),
    checkout,
    tracking
  };
};

export const getPriceCheckoutSession = async ({
  subdomain,
  price,
  cancelUrl,
  checkout,
  tracking
}) => {
  const requestParams = {
    subdomain,
    price,
    cancelUrl,
    checkout,
    tracking
  };
  devLogger("getPriceCheckoutSession.request.params", requestParams);
  try {
    const { data } = await axios({
      method: "post",
      url: API_PATHS.CHECKOUT,
      data: requestParams
    });

    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Fetch checkout session for subdomain price
 * - The redirect to checkout
 * @param {Object} params
 * @param {Object} params.stripe
 * @param {Function} params.setLoading
 * @param {String} params.subdomain
 * @param {String} params.price
 */
export const checkoutPrice = ({
  stripe,
  setLoading,
  subdomain,
  price,
  checkout,
  tracking
}) => async () => {
  setLoading(true);
  try {
    const session = await getPriceCheckoutSession(
      getSessionParams({
        subdomain,
        price,
        checkout,
        tracking
      })
    );
    if (stripe) {
      stripe.redirectToCheckout({
        sessionId: session.id
      });
    } else {
      captureException(new Error("No Stripe for price checkout"));
    }
    setLoading(false);
  } catch (error) {
    setLoading(false);
    throw error;
  }
};

export const checkoutShortLink = async ({
  stripe,
  url,
  setLoading,
  setError,
  setContent,
  xHeaders
}) => {
  setLoading(true);
  try {
    const requestInput = {
      method: "get",
      url
    };
    if (!isEmpty(xHeaders)) {
      requestInput.headers = xHeaders;
    }

    const { data } = await axios(requestInput);
    if (data) {
      if (data.session && data.session.id) {
        if (stripe) {
          stripe.redirectToCheckout({
            sessionId: data.session.id
          });
        } else {
          captureException(new Error("No Stripe for price checkout"));
        }
      } else if (data.content) {
        setContent(data.content);
      }
    }

    setLoading(false);
  } catch (error) {
    setLoading(false);
    setError(error);
  }
};

// =================================================
// BILLING
// https://stripe.com/docs/api/customer_portal/create
// =================================================
export const buildBillingParams = ({ customer }) => ({
  customer,
  return_url: typeof window !== "undefined" ? window.location.href : ""
});

export const getBillingSession = async (session) => {
  const PUB_KEY = getConfig("PRICE_BLOCS_PUB_KEY");

  try {
    const { data } = await axios({
      method: "post",
      url: API_PATHS.BILLING_SESSION,
      headers: {
        Authorization: `Bearer ${PUB_KEY}`
      },
      data: {
        session
      }
    });

    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Fetch billing session
 * - Then redirect to billing portal page
 * @param {Object} params
 * @param {Object} params.session
 * @param {Function} params.setLoading
 */
export const billing = ({ session, setLoading, setError }) => async () => {
  setLoading(true);
  try {
    const billingSession = await getBillingSession(session);
    if (isError(billingSession)) {
      setError && setError(billingSession);
      captureException(billingSession);
    } else if (billingSession && billingSession.url) {
      window.location.href = billingSession.url;
    }

    setLoading(false);
  } catch (error) {
    setError && setError(error);
    setLoading(false);
    throw error;
  }
};

// =================================================
// CHECKOUT
// https://stripe.com/docs/api/checkout/sessions/object
// ================================================
export const buildCheckoutParams = ({
  customer,
  resource,
  resourceUUID,
  successUrl
}) => {
  let success_url = successUrl
    ? `${successUrl}?${STRIPE_SESSION_ID_QS_PARAM}`
    : "";

  if (typeof window !== "undefined") {
    const hrefParts = window.location.href.split("?");
    if (!success_url) {
      success_url = `${hrefParts[0]}?${STRIPE_SESSION_ID_QS_PARAM}`;
      if (hrefParts[1]) {
        success_url += `&${hrefParts[1]}`;
      }
    } else {
      success_url += `&return_to=${window.location.pathname}`;
    }
  }

  return {
    customer,
    client_reference_id: resourceUUID,
    success_url,
    /**
     * Set resource in subscription metadata so that we can perform a lookup and association when processing webhooks
     */
    subscription_data: {
      metadata: {
        resourceUUID,
        resource
      }
    }
  };
};

export const getCheckoutSession = async (session) => {
  const PUB_KEY = getConfig("PRICE_BLOCS_PUB_KEY");
  try {
    const { data } = await axios({
      method: "post",
      url: API_PATHS.CHECKOUT_SESSION,
      headers: {
        Authorization: `Bearer ${PUB_KEY}`
      },
      data: {
        session
      }
    });

    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Fetch checkout session
 * - Then redirect to pricing page with encrypted session
 * @param {Object} params
 * @param {Object} params.session
 * @param {Function} params.setLoading
 */
export const checkout = ({ session, setLoading, setError }) => async () => {
  setLoading(true);
  try {
    const sealedSession = await getCheckoutSession(session);
    let href = `${getConfig("WEB_CLIENT_ROOT")}${PATHS.PRICING}`;

    if (isError(sealedSession)) {
      setError && setError(sealedSession);
      captureException(sealedSession);
    } else {
      href += `?session=${sealedSession}`;
    }
    window.location.href = href;

    setLoading(false);
  } catch (error) {
    setError && setError(error);
    setLoading(false);
    throw error;
  }
};

export const getCheckoutSuccessPreview = async ({ subdomain }) => {
  /* eslint-disable-next-line no-useless-catch */
  try {
    const url = `${API_PATHS.CHECKOUT_SUCCESS_PREVIEW}?subdomain=${subdomain}`;

    const response = await axios({
      url,
      method: "get",
      headers: authBearerToken()
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCheckoutSuccessAPIRoute = ({ pathname, query }) => {
  const path = isTestRoute(pathname)
    ? API_PATHS.TEST_CHECKOUT_SUCCESS
    : API_PATHS.CHECKOUT_SUCCESS;

  return `${path}?${QS.stringify(query)}`;
};

export const getCheckoutContentAPIRoute = ({ pathname, query }) => {
  const path = isTestRoute(pathname)
    ? API_PATHS.TEST_CHECKOUT_CONTENT
    : API_PATHS.CHECKOUT_CONTENT;

  return `${path}?${QS.stringify(query)}`;
};

export const getCheckoutSuccessServerSideProps = async ({ req, query }) => {
  /**
   * 1. Get subdomain params for query
   */
  const params = subdomainParamsFromReq({ req });

  /**
   * 2. Get test / live link request route
   * - extended with query params
   * - e.g. /v1/stripe/checkout/success?sessionId=abc
   */
  const REQUEST_URL = getCheckoutSuccessAPIRoute({
    pathname: req.url,
    query: {
      subdomain: params.subdomain,
      sessionId: query.session_id
    }
  });

  /**
   * 3. Render checkout success page summary for live / test context
   * - This is a GET to the provided url
   * - Generate the checkout payload on the server to prevent leaking of Stripe models to the client
   */
  const data = await fetchPageResponse(REQUEST_URL);

  /**
   * 4. Prepare response shape data
   */
  const response = preparePageResponse({
    data,
    params,
    keys: PAGE_KEYS.CHECKOUT.SUCCESS,
    errorContent: CHECKOUT_SESSION_ERROR_CONTENT
  });

  return {
    props: response
  };
};

export const getCheckoutContentServerSideProps = async ({ req, query }) => {
  /**
   * 1. Get subdomain params for query
   */
  const params = subdomainParamsFromReq({ req });

  /**
   * 2. Get test / live link request route
   * - extended with query params
   * - e.g. /v1/stripe/checkout/content?sessionId=abc
   */
  let data = {};
  let errorContent = CHECKOUT_CONTENT_ERROR_CONTENT;
  if (query.content) {
    const REQUEST_URL = getCheckoutContentAPIRoute({
      pathname: req.url,
      query: {
        subdomain: params.subdomain,
        content: query.content
      }
    });

    /**
     * 3. Render checkout content page summary for live / test context
     * - This is a GET to the provided url
     * - Generate the checkout payload on the server to prevent leaking of Stripe models to the client
     */
    data = await fetchPageResponse(REQUEST_URL);
  } else {
    data = new Error("No content");
    errorContent = CHECKOUT_CONTENT_NO_DATA_CONTENT;
  }

  /**
   * 4. Prepare response shape data
   */
  const response = preparePageResponse({
    data,
    params,
    keys: PAGE_KEYS.CHECKOUT.CONTENT,
    errorContent
  });

  return {
    props: response
  };
};

export const getRedirectUrls = ({ application, merchantAccount }) => {
  const redirectRoot = getSubdomainRoot(application.subdomain);
  /**
   * Success URLs - after successful checkout payment
   * 1. Return to the seller configured checkout success url
   * 2. Return to the generic checkout success url
   * - NOTE: if using this url we need to have the application subdomain in the root (i.e. subdomain redirect root)
   * - This allows us to fetch the session on behalf of that account
   */
  const configSuccessURL = get(
    merchantAccount,
    "merchant_account_config.data.common.success_url"
  );
  const fallbackSuccessURL = `${redirectRoot}${PATHS.CHECKOUT_SUCCESS}`;
  const successUrl = defaultHttps(configSuccessURL || fallbackSuccessURL);

  /**
   * Cancel URLs
   * 1. Return to the url of the sellers site within the merchant account
   * 2. Return to the seller configured merchant acccount checkout cancel url
   */
  const merchantRootURL = get(merchantAccount, "profile.url");
  const configCancelURL = get(
    merchantAccount,
    "merchant_account_config.data.common.cancel_url"
  );
  const fallbackErrorURL = `${redirectRoot}${PATHS.CHECKOUT_ERROR}`;
  const cancelUrl = defaultHttps(
    merchantRootURL || configCancelURL || fallbackErrorURL
  );

  return {
    successUrl,
    cancelUrl
  };
};
